import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { PATHURLDOMAIN } from "../../constantes/constantes";
import TurnContent from "./turnContent";
import CardResultsMobile from "./cardResultsMobile";
import NotesSkeletonLoader from "../skeleton/notesSkeletonLoader";
import "./abstract.sass";
import "./meta.sass";
import "./cardResults.sass";

const VIEWPORT_TOP_GAP = 16;

function NewConversationButton({ onClick, isVisible }) {
  return (
    <div className="container-btn-new-conversation">
      <button
        className={`buscador_btn-new-conversation${isVisible ? "" : " is-hidden"}`}
        onClick={onClick}
        title="Iniciar nueva conversación"
        aria-label="Borrar historial e iniciar nueva conversación"
        aria-hidden={!isVisible}
        tabIndex={isVisible ? undefined : -1}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M20.707 5.826l-2.533-2.533a1 1 0 0 0-1.414 0L7.05 12.003l-.03.03L5.5 18.5l6.467-1.52.03-.03 9.71-9.71a1 1 0 0 0 0-1.414zM11.302 15.88L7.5 16.5l.62-3.802 7.593-7.593 3.182 3.182-7.593 7.593zM3 20h18v1.5H3V20z" />
        </svg>
        <span className="btn-new-conversation-text">Nueva conversación</span>
      </button>
    </div>
  );
}

function TurnNotes({ turn, isMobile }) {
  const [showAll, setShowAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hits = Array.isArray(turn.hits) ? turn.hits : [];
  const notesId = `resultadosUTV-${turn.id}`;

  const sortedData = useMemo(
    () => [...hits].sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date)),
    [hits]
  );
  
  // Mostrar todos los hits sin restricciones
  const visibleData = sortedData;

  const hasNotes = sortedData.length > 0;

  const handleToggle = () => {
    setIsTransitioning(true);
    window.setTimeout(() => {
      setShowAll(previous => !previous);
      setIsTransitioning(false);
    }, 300);
  };

  if (!turn.notesReady) {
    return (
      <NotesSkeletonLoader
        count={hits.length}
        turnId={turn.id}
      />
    );
  }

  // Una respuesta vacía o fallida no ocupa espacio en la conversación.
  if (turn.notesError || !hasNotes) {
    return null;
  }

  return (
    <section
      className={`list-wrapper-results${showAll ? " expanded" : ""}`}
    >
      <CardResultsMobile
        data={visibleData}
        id={notesId}
        ariaLabel={`Resultados relacionados con ${turn.query}`}
      />
    </section>
  );
}

/** Renderiza una fila independiente por turno: respuesta y recomendaciones asociadas. */
export default function Results({
  conversation,
  isMobile,
  onNewConversation,
}) {
  const parentBase = useMemo(
    () => (PATHURLDOMAIN ? PATHURLDOMAIN.replace(/\/+$/, "") : ""),
    []
  );
  const [isNewConversationButtonVisible, setIsNewConversationButtonVisible] = useState(true);
  const lastTurnId = conversation[conversation.length - 1]?.id;

  useEffect(() => {
    const scrollDirectionThreshold = 8;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= VIEWPORT_TOP_GAP) {
        setIsNewConversationButtonVisible(true);
        lastScrollY = currentScrollY;
        return;
      }

      if (Math.abs(currentScrollY - lastScrollY) < scrollDirectionThreshold) return;

      setIsNewConversationButtonVisible(currentScrollY < lastScrollY);
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Un único desplazamiento al crear un turno; el streaming posterior no mueve la vista.
  useLayoutEffect(() => {
    if (!lastTurnId) return;

    const turnElement = document.querySelector(`[data-turn-id="${lastTurnId}"]`);
    if (!turnElement) return;

    // Busca el .conversation-row padre para hacer scroll al inicio completo del turno.
    const row = turnElement.closest('.conversation-row') || turnElement;

    // Calcula el offset del header fijo del sitio para no quedar tapado.
    const siteHeader = document.querySelector('.header-main, header, [class*="header"]');
    const headerOffset = siteHeader ? siteHeader.getBoundingClientRect().height : 0;
    const totalOffset = headerOffset + VIEWPORT_TOP_GAP;

    window.scrollTo({
      top: Math.max(
        0,
        window.scrollY + row.getBoundingClientRect().top - totalOffset
      ),
      behavior: "auto",
    });
  }, [lastTurnId]);

  if (!conversation || conversation.length === 0) return null;

  return (
    <div className="results-container">
      <div className="container-full-results">
        {conversation.map((turn, index) => (
          <div className="conversation-row" key={turn.id}>
            <div className="summary-card-wrapper">
              {index === 0 && (
                <NewConversationButton
                  onClick={onNewConversation}
                  isVisible={isNewConversationButtonVisible}
                />
              )}
              <section
                className="summary-card conversation-card"
                aria-label={`Respuesta para ${turn.query}`}
              >
                <div className="line-upsale" />
                <div className="dots_border" />
                <TurnContent
                  turn={turn}
                  parentBase={parentBase}
                  isLast={index === conversation.length - 1}
                />
              </section>
            </div>
            <TurnNotes
              turn={turn}
              isMobile={isMobile}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
